/**
 * The intake rules: what each kind of place takes. Every check is name-aware
 * and never MIME-only, because most systems give research code (.R, .jl, .do)
 * no MIME type, and Windows can label a .docx `application/octet-stream`.
 * The picker's `accept` lists extensions for the same reason: a MIME entry
 * there greys out the very file a researcher came to pick.
 */

import {
  ATTACHMENT_ACCEPT_EXTENSIONS,
  ATTACHMENT_ACCEPT_TYPES,
  isAcceptedAttachment,
} from '@bower/shared/schemas/upload.schema';
import {
  CSV_MAX_BYTES,
  SPREADSHEET_INTAKE_NOTE,
  SPREADSHEET_MAX_BYTES,
  classifySpreadsheetFile,
} from '@bower/shared/constants/spreadsheet-intake';
import { getFileExtension, supportsOCR } from '@bower/shared/utils/fileTypeDetection';

import type { DragVerdict, IntakeRule } from './types';
import { formatMegabytes } from './validateFiles';

/** A type the OS left blank or generic says nothing either way. */
const isOpaqueType = (mimeType: string) =>
  mimeType === '' || mimeType === 'application/octet-stream';

/** A drag verdict from a set of MIME types the rule knows it takes. */
const knownTypes =
  (accepts: (mimeType: string) => boolean) =>
  (mimeType: string): DragVerdict => {
    if (isOpaqueType(mimeType)) return 'unknown';
    return accepts(mimeType) ? 'yes' : 'no';
  };

export const ANY_FILE: IntakeRule = {
  test: () => null,
  dragType: () => 'yes',
  inputAccept: '',
  typeLabels: ['Any file'],
};

const extensions = (list: readonly string[]) =>
  list.map((ext) => (ext.startsWith('.') ? ext : `.${ext}`)).join(',');

/**
 * What can be attached to a note, a message or a protocol step. A MIME type
 * the map does not list is `unknown`, not `no`: the extension arm is what
 * admits research code, and a drag cannot see the extension.
 */
export const LAB_FILE: IntakeRule = {
  test: (file) => (isAcceptedAttachment(file.name, file.type) ? null : 'file type not supported'),
  dragType: (mimeType) =>
    Object.prototype.hasOwnProperty.call(ATTACHMENT_ACCEPT_TYPES, mimeType) ? 'yes' : 'unknown',
  inputAccept: extensions(ATTACHMENT_ACCEPT_EXTENSIONS),
  typeLabels: ['Any lab file'],
};

const HEIF_EXTENSIONS: readonly string[] = ['heic', 'heif'];

/**
 * An iPhone HEIC/HEIF the browser left untyped, given its real type. IMAGE
 * admits it by name, but it would upload as `application/octet-stream`, which
 * the server's OCR predicate (`supportsOCR`, no HEIF extensions by design)
 * refuses, so the photo note would stay empty. Any other file is returned as is.
 */
export function typeImageByName(file: File): File {
  const extension = getFileExtension(file.name);
  if (file.type || !HEIF_EXTENSIONS.includes(extension)) return file;
  return new File([file], file.name, {
    type: `image/${extension}`,
    lastModified: file.lastModified,
  });
}

/**
 * An image Bower can read text from. The one rule whose picker filter is a
 * MIME family, not extensions: `image/*` is what makes a phone offer its
 * photo library, and Safari types an iPhone's HEIC photo `image/heic`.
 */
export const IMAGE: IntakeRule = {
  // HEIC by name too: a desktop browser without the HEIF codec types it ''.
  test: (file) =>
    supportsOCR(file.name, file.type) || HEIF_EXTENSIONS.includes(getFileExtension(file.name))
      ? null
      : 'not an image',
  dragType: knownTypes((mimeType) => mimeType.startsWith('image/')),
  inputAccept: 'image/*',
  typeLabels: ['JPG', 'PNG', 'HEIC'],
  camera: true,
};

const LOGO_EXTENSIONS: readonly string[] = ['png', 'jpg', 'jpeg', 'webp', 'svg'];
const LOGO_TYPES: readonly string[] = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

/**
 * A workspace logo: what the server's logo route decodes (PNG, JPEG, WebP),
 * plus SVG, which the browser draws into a PNG first (`rasteriseSvgLogo`) so
 * the server never sees one. No HEIC (the server cannot read it).
 */
export const LOGO_IMAGE: IntakeRule = {
  test: (file) =>
    LOGO_EXTENSIONS.includes(getFileExtension(file.name))
      ? null
      : 'use a PNG, JPEG, WebP or SVG image',
  dragType: knownTypes((mimeType) => LOGO_TYPES.includes(mimeType)),
  inputAccept: extensions(LOGO_EXTENSIONS),
  typeLabels: ['PNG', 'JPEG', 'WebP', 'SVG'],
};

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * A source document a protocol's steps are read out of: PDF or .docx, never
 * legacy .doc. Judged by extension; the MIME decides only for a file with no
 * extension, and `application/msword` (which Windows also gives .rtf) never does.
 */
export const PROTOCOL_DOC: IntakeRule = {
  test: (file) => {
    const ext = getFileExtension(file.name);
    const ok = ext
      ? ext === 'pdf' || ext === 'docx'
      : file.type === 'application/pdf' || file.type === DOCX_MIME;
    return ok ? null : 'not a PDF or Word (.docx) file';
  },
  dragType: knownTypes((mimeType) => mimeType === 'application/pdf' || mimeType === DOCX_MIME),
  inputAccept: '.pdf,.docx',
  typeLabels: ['PDF', 'DOCX'],
};

const SPREADSHEET_MIMES = new Set([
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

export const SPREADSHEET: IntakeRule = {
  test: (file) => {
    const verdict = classifySpreadsheetFile(file.name, file.size);
    if (verdict.kind === 'importable') return null;
    if (verdict.kind === 'not-tabular') return 'not a CSV or Excel (.xlsx) file';
    return verdict.note === SPREADSHEET_INTAKE_NOTE.MACRO_WORKBOOK
      ? 'a macro-enabled workbook; save a copy as .xlsx'
      : `too large to open as a spreadsheet (${formatMegabytes(CSV_MAX_BYTES)} for CSV, ${formatMegabytes(SPREADSHEET_MAX_BYTES)} for Excel)`;
  },
  // Windows reports a .csv as application/vnd.ms-excel, so that type is not a "no".
  dragType: (mimeType) => {
    if (SPREADSHEET_MIMES.has(mimeType)) return 'yes';
    if (isOpaqueType(mimeType) || mimeType === 'application/vnd.ms-excel') return 'unknown';
    return 'no';
  },
  inputAccept: '.csv,.xlsx',
  typeLabels: ['CSV', 'XLSX'],
  sizeLabel: `CSV to ${formatMegabytes(CSV_MAX_BYTES)}, XLSX to ${formatMegabytes(SPREADSHEET_MAX_BYTES)}`,
};
